import React from 'react';
import { pageCount, paginateBookings } from '../libs/listViewPresentation';
import type {
  ActiveFilter,
  ListViewBooking,
  ListViewDate
} from '../listview/types';

interface ListViewPagination {
  page: number;
  pagesCount: number;
  /* The bookings of the current page */
  pageBookings: ListViewBooking[];
  onPageChange: (page: number) => void;
}

/* Client-side paging over the (already filtered) bookings of a list view.
 *
 * The page is not persisted anywhere and nothing outside the view reads it, so
 * it lives here. It resets whenever the set of bookings on offer changes — the
 * day, the filters, whether past bookings are left out — keyed on the values
 * rather than on the arguments' identities: a new array or a new date object
 * holding the same day must not kick the user back to page 1. */
const useListViewPagination = (
  bookings: ListViewBooking[],
  itemsPerPage: number,
  date: ListViewDate,
  activeFilters: ActiveFilter[],
  hidePastBookings: boolean
): ListViewPagination => {
  const [page, setPage] = React.useState(1);
  const bookingSelectionKey = JSON.stringify([
    date.valueOf(),
    activeFilters.map((filter) => [filter.id, filter.fieldKeyword, filter.values]),
    hidePastBookings
  ]);
  React.useEffect(() => {
    setPage(1);
  }, [bookingSelectionKey]);

  const pagesCount = pageCount(bookings.length, itemsPerPage);
  /* Clamp defensively: filtering can lower the number of pages below the
     current page */
  const currentPage = Math.min(page, pagesCount);
  return {
    page: currentPage,
    pagesCount,
    pageBookings: paginateBookings(bookings, currentPage, itemsPerPage),
    onPageChange: setPage
  };
};

export default useListViewPagination;
